import { createTrackingUtils } from "../index";
import useProfiler from "../../../hooks/useProfiler";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { UIFlightData } from "@flights/types";
import useIsInViewport from "../../../hooks/useIsInViewport";

const util = createTrackingUtils({
  name: "flights_web_aa_sr_badges_visibility_desktop",
  defaultStage: 1,
  stages: {
    main: 1,
    allbadges: 2,
    onlybest: 3,
    onlycheapest: 4,
    onlyfastest: 5,
    onlycheapestdirect: 6,
    midLong: 7,
    short: 8
  },
  goals: {
    onlyBest: 1,
    onlyCheapest: 2,
    onlyFastest: 3,
    onlyCheapestDirect: 4
  },
  goalsWithValue: []
});

export default function useFlightsWebAaSrBadgesVisibilityDesktop(ref: RefObject<HTMLDivElement>, flight: UIFlightData) {
  const [isTracked, setIsTracked] = useState(false);
  const isInViewport = useIsInViewport(ref);
  const { isDesktop, isMeta, isLongHaul, isMediumHaul, isShortHaul } = useProfiler();

  const canTrack = useMemo(() => isDesktop() && !isMeta(), [isDesktop, isMeta]);

  const badges = useMemo(() => {
    const list = ["BEST", "CHEAPEST", "FASTEST", "CHEAPEST_DIRECT"];
    return flight.badges?.map((b) => b.type).filter((b) => list.includes(b)) || [];
  }, [flight]);

  const trackStages = useCallback(() => {
    util.stages.main();
    if (badges.length === 1) {
      if (badges.includes("BEST")) util.stages.onlybest();
      if (badges.includes("CHEAPEST")) util.stages.onlycheapest();
      if (badges.includes("FASTEST")) util.stages.onlyfastest();
      if (badges.includes("CHEAPEST_DIRECT")) util.stages.onlycheapestdirect();
    }
    if (isLongHaul()) util.stages.midLong();
    if (isMediumHaul()) util.stages.midLong();
    if (isShortHaul()) util.stages.short();
  }, [badges, isLongHaul, isMediumHaul, isShortHaul]);

  const trackOnClickVisibility = useCallback(() => {
    if (!canTrack) return;
    if (badges.length !== 1) return;
    if (badges.includes("BEST")) util.goals.onlyBest();
    if (badges.includes("CHEAPEST")) util.goals.onlyCheapest();
    if (badges.includes("FASTEST")) util.goals.onlyFastest();
    if (badges.includes("CHEAPEST_DIRECT")) util.goals.onlyCheapestDirect();
  }, [canTrack, badges]);

  useEffect(() => {
    if (!isInViewport || isTracked || !canTrack) return;
    trackStages();
    setIsTracked(true);
  }, [isInViewport, isTracked, trackStages, canTrack]);

  return { trackOnClickVisibility };
}
